import React, { useEffect, useState, memo } from 'react';
import Pagination from '../Libs/Pagination';
import { getDefaultError, getDefaultHire } from './common';
import api from 'services';
import Filters from './Filters';
import { showNotification } from 'helpers';
import _ from 'lodash';
import { CircularProgress, RadioButton, RadioButtonGroup } from 'material-ui';
import styles from 'styles';
import HiresTable from './HiresTable';
import * as immer from 'immer';
import ExportDialog from './ExportDialog';
import { HiresExportFieldsMapping } from 'constants/hire';

function transformArray(array) {
    const validFields = Array.from(HiresExportFieldsMapping.keys()).filter(key => 
        array.some(obj => _.has(obj, key)));

    return array.map((obj) => {
        const transformedEntries = validFields
            .map((key) => {
                const value = _.get(obj, key);
                return [
                    HiresExportFieldsMapping.get(key).placeHolder,
                    value === null || value === undefined || !value.toString() ? '-' : value,
                ];
            })
            .filter(Boolean);

        return Object.fromEntries(transformedEntries);
    });
}

export const AddUpdateContext = React.createContext({});
export const ChangeFieldsContext = React.createContext(undefined);
export const RemoveRowContext = React.createContext(undefined);
export const HandleUpdateErrorsContext = React.createContext(undefined);
export const VesselDwtTypesContext = React.createContext(undefined);
export const CargoTypesContext = React.createContext(undefined);
export const CurrentTypeByPurposeContext = React.createContext(undefined);

function HiresPage() {
    const rowsPerPage = [25, 50, 100];
    const [total, setTotal] = useState(0);
    const [errors, setErrors] = useState([]);
    const [rows, setRows] = useState([]);
    const [rowsChanged, setRowsChanged] = useState([]);
    const [page, setPage] = useState(1);
    const [numberOfRows, setNumberOfRows] = useState(25);
    const [filters, setFilters] = useState(/** @type {import('./Filters').Filters} */ ({ typeByPurpose: 'MPP' }));
    const [isLoaded, setIsLoaded] = useState(false);
    const [exportDialogOpened, setExportDialogOpened] = useState(false);
    const [vesselDwtTypes, setVesselDwtTypes] = useState([]);
    const [cargoTypes, setCargoTypes] = useState([]);

    useEffect(() => {
        api.getVesselDwtTypes()
            .then((res) => {
                setVesselDwtTypes(res.data);
            });

        api.getCargoTypes()
            .then(res => setCargoTypes(res.data));   
    }, []);

    const getData = (_filters) => {
        setIsLoaded(false);
        api.getHiresList(_filters)
            .then((res) => {
                if (!res.errors || !res.errors.length) {
                    setRows(res.data.items);
                    setTotal(res.data.total);
                    setErrors(res.data.items.map(r => getDefaultError(r)));
                } else {
                    showNotification(res.errors[0].messages[0], 'error');
                }

                setIsLoaded(true);
            });
    };

    useEffect(() => {
        getData({ 
            ...filters,
            page, 
            limit: numberOfRows,
        }); 
    }, [filters, page, numberOfRows]);

    const handleUpdatePagination = ({ page: p, numberOfRows: n }) => {
        if (numberOfRows !== n) {
            setPage(1);
        } else {
            setPage(p);
        }
        setNumberOfRows(n);
        setRowsChanged([]);
    };

    const updateRows = async () => {
        if (!rowsChanged.length) return;

        if (errors.find(e => Object.values(_.omit(e, ['_id'])).find(_e => _e))) {
            showNotification('Please fill in all required fields.', 'error');
            return;
        }

        const toUpdate = rowsChanged.map((change) => {
            const changeForSave = _.cloneDeep(change);

            const result = {
                ...changeForSave,
                delivery: changeForSave.delivery ? changeForSave.delivery._id : undefined,
                redelivery: changeForSave.redelivery ? changeForSave.redelivery._id : undefined,
                mainRegionDelivery: changeForSave.mainRegionDelivery ? changeForSave.mainRegionDelivery._id : undefined,
                mainRegionRedelivery: changeForSave.mainRegionRedelivery ? changeForSave.mainRegionRedelivery._id : undefined,
                vesselId: changeForSave.vessel ? changeForSave.vessel._id : undefined,
                hire: parseFloat(changeForSave.hire),
                cargoId: changeForSave.cargo ? changeForSave.cargo._id : undefined,
                cargoTypeId: changeForSave.cargoTypeId ? changeForSave.cargoTypeId._id : undefined,
                cargoRegionId: changeForSave.cargoRegionId ? changeForSave.cargoRegionId._id : undefined,
                fuelPrice: changeForSave.fuelPrice,
                vesselDwtGroup: changeForSave.vesselDwtGroup._id,
                bunkerPlace: changeForSave.bunkerPlace ? changeForSave.bunkerPlace._id : undefined,
                typeByPurpose: filters.typeByPurpose,
            };

            delete result.vessel;

            return result;
        });

        const response = await api.updateHireIdeas({ update: toUpdate });
        if (!response.errors || !response.errors.length) {
            showNotification('Added successfully.', 'success');
            setPage(1);
            getData({
                page: 1, limit: numberOfRows, ...filters, 
            });
            setRowsChanged([]);
        } else {
            showNotification(response.errors[0].messages[0], 'error');
        }
    };

    /**
     * @param {*} value 
     * @param {{ clear?: boolean }} opts 
     */
    const changeFilters = (value, { clear = false } = {}) => {
        setFilters({
            ...(clear ? {} : filters), page: 1, ...value, 
        });
        setPage(1);
        setRows([]);
        setRowsChanged([]);
    };

    const renderPagination = (
        <Pagination
            total={total}
            rowsPerPage={rowsPerPage}
            page={page}
            numberOfRows={numberOfRows}
            updateRows={handleUpdatePagination}
        />
    );

    const addRow = () => {
        const defaultHire = getDefaultHire();
        
        setRows((currentRows) => {
            const newRows = [defaultHire, ...currentRows];
            return newRows;
        });
        
        setRowsChanged((currentChanged) => {
            const newChanged = [defaultHire, ...currentChanged];
            return newChanged;
        });
        
        setErrors(currentErrors => [
            getDefaultError({ _id: defaultHire.uniqueId }, { isNew: true }), 
            ...currentErrors,
        ]);
    };
    
    function changeFieldsMultiple(changes) {
        const newRows = immer.produce(rows, (_rows) => {
            changes.forEach((change) => {
                const { index, field, value } = change;
                if (typeof index !== 'number' || index < 0 || index >= _rows.length) {
                    console.warn('Invalid index:', index, 'rows length:', _rows.length);
                    return;
                }
                _.set(_rows[index], field, value);
            });
        });
    
        const newChanged = immer.produce(rowsChanged, (_rowsChanged) => {
            changes.forEach((change) => {
                const { index } = change;
                if (!newRows[index]) {
                    console.warn('Row not found at index:', index);
                    return;
                }
    
                const changedRow = { ...newRows[index] };
                const alreadyInChanged = _rowsChanged.findIndex((rC) => {
                    const rowId = rC._id;
                    const newRowId = changedRow._id;
                    console.log('Comparing IDs:', {
                        rowId,
                        newRowId,
                        matches: rowId === newRowId,
                    });
                    return rowId === newRowId;
                });
    
                if (alreadyInChanged >= 0) {
                    _rowsChanged[alreadyInChanged] = changedRow;
                } else {
                    _rowsChanged.push(changedRow);
                }
            });
        });
    
        setRows([...newRows]);
        setRowsChanged([...newChanged]);
    }

    const removeRow = (rowId) => {
        const newRows = immer.produce(rows, (_rows) => {
            _rows.splice(rowId, 1);
        });

        const newErrors = immer.produce(errors, (_errors) => {
            _errors.splice(rowId, 1);
        });

        setRows(newRows);
        setErrors(newErrors);
    };

    const handleUpdateErrors = async (index, field, value) => {
        if (errors.length) {
            errors[index] && setErrors((prev) => {
                const newErrors = immer.produce(prev, (_errors) => {
                    _errors[index][field] = value;
                });
                return newErrors;
            });
        }
    };

    /**
     * @param {'csv' | 'json'} extention 
     * @param {Record<string, 1 | -1>} select 
     */
    const exportRows = async (extention, select) => {
        const response = await api.exportHireIdeas(extention, { ...filters, page, limit: numberOfRows }, select);

        if (!Array.isArray(response.data)) {
            showNotification(response.errors[0].messages[0], 'error');
            return;
        }

        if (extention === 'json') {
            const jsonContent = JSON.stringify(response.data, null, 2);
            const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Hires.json'; 
            a.click();
            URL.revokeObjectURL(url);
        } else {
            const transformedData = transformArray(response.data);

            const escapeCsvValue = (value) => {
                if (value === null || value === undefined || !value.toString()) return null;
                const stringValue = String(value);
                return `"\u200E${stringValue.replace(/"/g, '""').trim()}\u200E"`;
            };

            const csvContent = [
                Object.keys(transformedData[0]).map(header => escapeCsvValue(header)).filter(Boolean).join(','),
                ...transformedData.map(row => 
                    Object.values(row).map(value => escapeCsvValue(value)).filter(Boolean).join(',')),
            ].join('\n');

            const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { 
                type: 'text/csv;charset=utf-8;', 
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Hires.csv'; 
            a.click();
            URL.revokeObjectURL(url);
        }
    };

    const handleOpenExportDialog = () => {
        setExportDialogOpened(!exportDialogOpened);  
    };

    const renderPurposeToggle = (
        <RadioButtonGroup
            onChange={(_e, v) => changeFilters({ typeByPurpose: v, vesselDwtGroup: undefined })}
            style={{ ...styles.radioGroup, display: 'flex', gap: '25px' }}
            name='typeByPurpose'
            valueSelected={filters.typeByPurpose}
        >
            {
                [{ value: '', label: 'All' }, { value: 'MPP', label: 'MPP' }, { value: 'BULK', label: 'BULK' }].map(e => (
                    <RadioButton
                        key={e.label}
                        style={{ ...styles.radioButton, width: 'unset' }}
                        value={e.value}
                        label={e.label}
                    />
                ))
            }
        </RadioButtonGroup>
    );

    return (
        <VesselDwtTypesContext.Provider value={vesselDwtTypes}>
            <div>
                <ExportDialog open={exportDialogOpened} handleOpenExportDialog={handleOpenExportDialog} exportRows={exportRows} />
                <div className='table-header' style={{ display: 'flex', alignItems: 'center' }}>
                    <div className='item'>
                        {renderPurposeToggle}
                    </div>
                    <div className='item'>
                        {renderPagination}
                    </div>
                </div>
                <div className="table-header" style={{ flexDirection: 'column' }}>
                    <AddUpdateContext.Provider value={{
                        addRow, updateRows, handleOpenExportDialog, 
                    }}
                    >
                        <Filters
                            filters={filters}
                            changeFilters={changeFilters}
                            clearFilters={() => changeFilters({ typeByPurpose: 'MPP' }, { clear: true })}
                        />
                    </AddUpdateContext.Provider>
                </div>
                <CircularProgress
                    className={!isLoaded ? '' : 'hidden'}
                    style={styles.circularProgress}
                />
                <div className={isLoaded ? '' : 'hidden'} style={{ marginTop: '10px' }}>
                    <div className="table-body">
                        <RemoveRowContext.Provider value={removeRow}>
                            <HandleUpdateErrorsContext.Provider value={handleUpdateErrors}>
                                <CargoTypesContext.Provider value={cargoTypes}>
                                    <CurrentTypeByPurposeContext.Provider value={filters.typeByPurpose}>
                                        <HiresTable rows={rows} errors={errors} changeFieldsMultiple={changeFieldsMultiple} />
                                    </CurrentTypeByPurposeContext.Provider>
                                </CargoTypesContext.Provider>
                            </HandleUpdateErrorsContext.Provider>
                        </RemoveRowContext.Provider>
                    </div>
                </div>
            </div>
        </VesselDwtTypesContext.Provider>
    );
}

export default memo(HiresPage);



// WEBPACK FOOTER //
// ./app/components/Hires/HiresPage.jsx